import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { adminAuditsByEmail, adminWhoami, deleteAudit } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(null); // null = checking, false = denied, {email,name} = allowed
  const [email, setEmail] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminWhoami()
      .then(setAuthed)
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return <div className="overline" data-testid="admin-loading">Loading…</div>;
  }
  if (authed === false) {
    return (
      <div data-testid="admin-denied" className="card-tech rounded-sm p-10">
        <div className="overline text-[var(--accent)]">Admin only</div>
        <h1 className="font-display text-3xl tracking-tight mt-2">Restricted area.</h1>
        <p className="mt-3 text-[var(--ink-2)]">
          This page is only for The Weave's team accounts.
        </p>
        <Button
          onClick={() => navigate("/app")}
          className="mt-6 rounded-full h-11 px-5"
        >
          Back to your workspace
        </Button>
      </div>
    );
  }

  const onLookup = async (e) => {
    e?.preventDefault?.();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !cleaned.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    try {
      const data = await adminAuditsByEmail(cleaned, { includeDeleted });
      setResult(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed.");
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (auditId) => {
    if (!window.confirm("Soft-delete this audit? It'll be hard-purged in 30 days.")) return;
    try {
      await deleteAudit(auditId); // admin's session cookie proves identity — but they need email match for delete.
      toast.success("Deleted (or attempted — re-run the lookup to confirm).");
      // Re-run the lookup so the row drops out.
      await onLookup();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Admin can't delete audits owned by others — ask the user to delete from their own link."
      );
    }
  };

  return (
    <div data-testid="admin-page" className="space-y-10">
      <div>
        <div className="overline">Admin · {authed.email}</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
          Audits by email.
        </h1>
        <p className="mt-3 text-[var(--ink-2)] max-w-xl">
          Look up every audit run under a given email — for support, debugging, or
          GDPR right-of-access requests.
        </p>
      </div>

      <form
        onSubmit={onLookup}
        className="card-tech rounded-sm p-6 flex flex-wrap gap-3 items-end"
        data-testid="admin-lookup-form"
      >
        <div className="flex-1 min-w-[260px]">
          <label className="overline block mb-2">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="founder@example.com"
            autoComplete="off"
            data-testid="admin-email-input"
            className="w-full h-11 rounded-sm border border-[var(--line)] px-3 font-mono-mark text-sm bg-[var(--paper)]"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--ink-2)] h-11">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            data-testid="admin-include-deleted"
          />
          Include soft-deleted
        </label>
        <Button
          type="submit"
          disabled={busy}
          data-testid="admin-lookup-btn"
          className="rounded-full h-11 px-5"
        >
          {busy ? "Looking up…" : "Look up"}
        </Button>
      </form>

      {result && (
        <div data-testid="admin-results">
          <div className="overline mb-3">
            {result.count} audit{result.count === 1 ? "" : "s"} for {result.email}
          </div>
          {result.audits.length === 0 ? (
            <div className="card-tech rounded-sm p-6 text-[var(--ink-2)]">
              No audits found for this email.
            </div>
          ) : (
            <ul className="card-tech rounded-sm divide-y divide-[var(--line)]">
              {result.audits.map((a) => (
                <li
                  key={a.audit_id}
                  className="grid grid-cols-12 gap-4 p-5 items-center"
                  data-testid={`admin-audit-${a.audit_id}`}
                >
                  <div className="col-span-1 font-display text-2xl">
                    {a.overall_score ?? "—"}
                  </div>
                  <div className="col-span-5">
                    <div className="font-mono-mark text-xs">{a.audit_id}</div>
                    <div className="text-xs text-[var(--muted)] truncate">
                      {a.linkedin_url || "—"}
                    </div>
                  </div>
                  <div className="col-span-2 text-xs text-[var(--muted)] font-mono-mark">
                    {a.status || "—"}
                  </div>
                  <div className="col-span-2 text-xs text-[var(--muted)] font-mono-mark">
                    {(a.created_at || "").slice(0, 10)}
                    {a.deleted_at && (
                      <div className="text-[var(--accent)] mt-1">
                        deleted {(a.deleted_at || "").slice(0, 10)}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex justify-end gap-2">
                    <a
                      href={`/results/${a.audit_id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-view-${a.audit_id}`}
                      className="text-xs font-mono-mark uppercase tracking-wider px-3 py-2 rounded-sm border border-[var(--line)] hover:bg-[var(--bg-2)]"
                    >
                      Open
                    </a>
                    {!a.deleted_at && (
                      <button
                        type="button"
                        onClick={() => onDelete(a.audit_id)}
                        data-testid={`admin-delete-${a.audit_id}`}
                        className="text-xs font-mono-mark uppercase tracking-wider px-3 py-2 rounded-sm bg-[var(--accent)] text-white"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
